import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { WorkspaceView } from '@lemoncloud/codes-goods-api';
import { useQueryClient } from '@tanstack/react-query';
import { useDialogStore, useUpdateQueryData, useUpdateTargetSite } from '@web/app/shared';

import { useLoader } from '@codes/loader';
import { useOwner } from '@codes/owner';
import { PaginationType } from '@codes/shared';
import { workspacesKeys } from '@codes/workspace';

interface UseCreateWorkspaceDialogProps {
    onComplete?: (newWorkspace: WorkspaceView) => void;
    onClose?: () => void;
}
export const useCreateWorkspaceDialog = ({ onComplete, onClose }: UseCreateWorkspaceDialogProps) => {
    const { fetchAndSetOwner } = useOwner();
    const { isWorkspaceDialogOpen, setWorkspaceDialog } = useDialogStore(state => state);

    const { setIsLoading } = useLoader();
    const updateTargetSite = useUpdateTargetSite();
    const navigate = useNavigate();

    const queryClient = useQueryClient();

    const { t } = useTranslation();
    const [newWorkspace, setNewWorkspace] = useState<WorkspaceView | null>(null);

    const updateQueryData = useUpdateQueryData<PaginationType<WorkspaceView[]>>();

    const handleComplete = async (newWorkspace: WorkspaceView) => {
        setWorkspaceDialog(false);
        onComplete?.(newWorkspace);
        setNewWorkspace(newWorkspace);
        updateQueryData({
            queryKey: workspacesKeys.list({}),
            updater: oldData => {
                if (!oldData) {
                    return { total: 1, data: [newWorkspace], page: 0 };
                }

                return {
                    ...oldData,
                    data: [newWorkspace, ...oldData.data],
                    list: [newWorkspace, ...oldData.data],
                    total: (oldData.total || 0) + 1,
                };
            },
        });

        // toast({
        //     description: t('workspace.create.workspaceCreationCompleted'),
        // });

        // navigate(`/home?siteId=${newWorkspace.siteId}`);
        // refresh(newWorkspace.siteId as string);
    };

    const refresh = async (siteId: string) => {
        setIsLoading(true);

        try {
            const result = await updateTargetSite(siteId);

            await queryClient.invalidateQueries({ queryKey: workspacesKeys.detail('0') });
            await fetchAndSetOwner();
        } catch (e) {
            console.error(e);
            navigate('/auth/logout', { replace: true });
        } finally {
            setIsLoading(false);

            // window.location.reload();
        }
    };

    const handleClose = () => {
        onClose?.();
        setWorkspaceDialog(false);
    };

    return { isWorkspaceDialogOpen, setWorkspaceDialog, handleClose, handleComplete, refresh, newWorkspace };
};
